import set from 'lodash/set'

import { db } from '../../services/firebase'

export default class Base {
  /**
   * @param {string} collection
   */
  constructor(collection) {
    this._collection = db.collection(collection)
  }

  /**
   * Get a document reference
   * @param {string} id
   * @returns {DocumentReference}
   */
  get(id) {
    return this._collection.doc(id)
  }

  /**
   * Get a document data
   * @param {string} id
   * @returns {Object}
   */
  async getData(id) {
    const ref = await this.get(id).get()

    return Object.assign({}, this._unflattenData(ref.data()), { id })
  }

  async getAll() {
    return this._collection.get()
  }

  /**
   * Create a document
   * @returns {Object}
   */
  async create(payload) {
    const data = this._buildData(payload)

    // Create the document
    const ref = await this._collection.add(data)

    // Add the document ID to the return value
    return Object.assign({}, data, { id: ref.id })
  }

  /**
   * Create or update a document
   * @param {string} documentId
   * @param {Object} payload
   * @returns {Object}
   */
  async set(documentId, payload) {
    if (!documentId) return Object.assign({}, data, { id: documentId })

    const data = this._buildData(payload)

    await this._collection.doc(documentId).set(data, { merge: true })

    return Object.assign({}, data, { id: documentId })
  }

  /**
   * Update the given document
   * @param {string} id
   * @param {Object} payload
   * @returns {Promise}
   */
  async update(id, payload) {
    const data = Object.assign({}, this._flattenPayload(payload), {
      updateDate: new Date().toISOString()
    })

    await this._collection.doc(id).update(data)
  }

  /**
   * Query Firestore with depending fields
   * @param {Object} fields
   * @returns {Array<Object>}
   */
  async queryFields(fields) {
    const query = this.buildQueryFields(fields)

    const result = await query.get()

    // Hydrate each document
    return result.docs.map(doc =>
      Object.assign({}, this._unflattenData(doc.data()), { id: doc.id })
    )
  }

  /**
   * Build a firestore query object
   * @param {Object} fields
   * @returns {firebase.firestore.Query}
   */
  buildQueryFields(fields) {
    // Build the query
    return Object.keys(fields).reduce(
      (query, field) => query.where(field, '==', fields[field]),
      this._collection
    )
  }

  /**
   * Build the data object for a document creation :
   *  - Add the user id
   *  - Add the creation date
   * @param {Object} payload
   * @returns {Object}
   */
  _buildData(payload) {
    return Object.assign({}, this._flattenPayload(payload), {
      creationDate: new Date().toISOString()
    })
  }

  /**
   * Flatten a payload to have a single level object
   * @param {Object} payload
   * @param {string} path
   * @returns {Object}
   */
  _flattenPayload(payload, path = '') {
    return Object.keys(payload).reduce((accumulator, key) => {
      const fullPath = path ? `${path}.${key}` : key
      const value = payload[key]

      if (!value) {
        accumulator[fullPath] = value

        return accumulator
      }

      if (typeof value !== 'object' || Array.isArray(value)) {
        accumulator[fullPath] = value

        return accumulator
      }

      return Object.assign({}, accumulator, this._flattenPayload(value, fullPath))
    }, {})
  }

  _unflattenData(data) {
    const object = {}

    Object.keys(data).forEach(key => set(object, key.split('.'), data[key]))

    return object
  }
}
